import React from 'react';
import Header from './Header';
import background from './img/softwarePartners.png';
import MobileNavigation from './MobileNavigation';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { CopyBlock, dracula } from 'react-code-blocks';
import IFrameConfigForm from './iFrameConfig';
import CustomDataTypes from './customDataTypes';
import StylesParams from './stylesParams';
import Footer from './Footer';

const StyledTabs = styled((props) => (
  <Tabs
    {...props}
    TabIndicatorProps={{
      children: <span className="MuiTabs-indicatorSpan" />
    }}
    variant="scrollable"
    scrollButtons
    allowScrollButtonsMobile
    centered
  />
))(({ theme }) => ({
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  '& .MuiTabs-indicatorSpan': {
    width: '100%',
    backgroundColor: '#1C7BC0'
  }
}));

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  color: '#000000',
  '&.Mui-selected': {
    color: '#000000',
    fontWeight: theme.typography.fontWeightMedium
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(100, 95, 228, 0.32)'
  }
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="span">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function TabProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}

const DeveloperTokenizer = () => {
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  return (
    <>
      <MobileNavigation />
      <section className="bg-[#212121]">
        <div
          className="w-full pb-28"
          style={{
            backgroundImage: `url(${background})`,
            backgroundSize: '200% 200%',
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Header />
          <div className="p-4 row md:p-8 how">
            <h3 className="text-white large">Tokenization</h3>
          </div>
          <div className="flex p-16 row flex-col justify-center flex-start gap-6 self-stretch rounded-[28px] bg-white !mb-10">
            <p className="text-black light">
              The purpose of the Tokenizer API is to provide a javascript implementation that
              injects hosted payment fields onto your website. By injecting the hosted payment page
              into an iframe via this library, a client remains outside the scope of PCI while still
              having full control over the processing of the transaction. Once the sensitive data
              has been collected and tokenized, you may submit any regular API call exchanging the
              sensitive information with the token you have received within 2 minutes.
            </p>

            <p className="text-black light">
              See the following pages on how to embed the Basys tokenizer into your workflows:
            </p>
            {/* TABS */}
            <Box sx={{ width: '100%' }}>
              <Box
                display="flex"
                width="100%"
                sx={{ borderBottom: 1, borderColor: 'divider', justifyContent: 'center' }}
              >
                <StyledTabs value={value} onChange={handleChange} aria-label="training video tabs">
                  <StyledTab label="Creating the iFrame" {...TabProps(0)} />
                  <StyledTab label="Custom Data Types" {...TabProps(1)} />
                  <StyledTab label="Styling iFrame" {...TabProps(2)} />
                </StyledTabs>
              </Box>
              <TabPanel value={value} index={0}>
                <h2 className="text-black large">Creating the iFrame</h2>
                <p className="text-black light">
                  Setting up the iFrame consists of three steps:
                  <ol className="ml-5 list-decimal">
                    <li className="text-black light">Generating an Authentication Key</li>
                    <li className="text-black light">Creating an iFrame configuration object</li>
                    <li className="text-black light">
                      Loading the iFrame into the DOM using the Basys iFrame JS library. To test out
                      the iFrame, the following script must be included into your page from this
                      URL:
                    </li>
                  </ol>
                </p>
                <p className="text-black light">Production:</p>
                <CopyBlock
                  text="<script src='https://api.basyspro.com/iframe/iframe/iframe-v3.js'/>"
                  theme={dracula}
                  language="html"
                  showLineNumbers={false}
                  customStyle={{ overflowX: 'auto', marginBottom: '1rem' }}
                />
                <p className="text-black light">Sandbox:</p>
                <CopyBlock
                  text="<script src='https://sandbox.api.basyspro.com/iframe/iframe/iframe-v3.js'/>"
                  theme={dracula}
                  language="html"
                  showLineNumbers={false}
                  customStyle={{ overflowX: 'auto', marginBottom: '1rem' }}
                />
                <h2 className="font-normal text-black">Generating the Authentication Key</h2>
                <p className="text-black light">
                  The first step in utilizing the iFrame is establishing an authentication key. This
                  is done by utilizing our token configuration API call.
                </p>
                <CopyBlock
                  text={`/gateway/{gatewayId}/tokenization/configuration`}
                  theme={dracula}
                  showLineNumbers={false}
                  customStyle={{ overflowX: 'auto', marginBottom: '1rem' }}
                />
                <p className="text-black light">
                  An origin header with a valid URL is required for the configuration endpoint. Ex:
                </p>

                <p className="text-black light">
                  <code>origin</code>:https://app.iqpro.com
                </p>

                <p className="text-black light">
                  For security purposes, the endpoint is required to generate adhoc public token for
                  the iFrame. Also note that the token is only valid for the 15mins duration. This
                  time can not be changed due to security reasons.
                </p>
                <p className="text-black light">Example Response:</p>
                <CopyBlock
                  text={`{
  "statusCode": "OK",
  "data": {
    "cardTokenizer": "IQPROV2",
    "achTokenizer": "IQPROV2",
    "iframeConfiguration": {
      "iqProV2": {
        "origin": "https://app.iqpro.com",
        "timestamp": "21001231235959",
        "tokenizationId": "<tokenization_id>",
        "tokenScheme": "PCI",
        "authenticationKey": "<Auth_Key>"",
        "pci": true,
        "expires": "2100-12-31T20:07:51.5519083Z"
      }
    },
    "proxyConfiguration": {},
    "achConfiguration": {
      "iqProV2": {
        "achUrl": "https://api.basyspro.com/vault/api/v1/Proxy/Ach"
      }
    },
    "mobileConfiguration": {
      "iqProV2": {
        "timestamp": "21001231235959",
        "tokenizationId": "<tokenization_id>",
        "tokenScheme": "PCI",
        "authenticationKey": "<Auth_Key>",
        "pci": true,
        "expires": "2100-12-31T20:07:51.5519083Z"
      }
    }
  }
}
`}
                  theme={dracula}
                  showLineNumbers={false}
                  language="json"
                  customStyle={{ overflowY: 'auto' }}
                />
                <p className="text-black light">
                  Make sure you use data specified in the <code>iframeConfiguration</code> object
                  above when building the configuration object.
                </p>
                <h2 className="font-normal text-black">Building the Configuration Object</h2>
                <p className="text-black light">
                  The next step is creating the iFrame configuration object itself, which consists
                  of the following parameters:
                </p>
                <IFrameConfigForm />
                <h2 className="font-normal text-black">Loading the iFrame</h2>
                <p className="text-black light">
                  Now that you've built your configuration object, the last step is to render the
                  iFrame on your form. Reference the Test Basys iFrame JS Library located at{' '}
                  <a
                    href="https://api.basyspro.com/Iframe/iframe-v3.js"
                    className="text-blue-500 underline"
                  >
                    https://api.basyspro.com/Iframe/iframe-v3.js
                  </a>{' '}
                  (production) or{' '}
                  <a
                    href="https://sandbox.api.basyspro.com/Iframe/iframe/iframe-v3.js"
                    className="text-blue-500 underline"
                  >
                    https://sandbox.api.basyspro.com/Iframe/iframe/iframe-v3.js
                  </a>{' '}
                  (sandbox) and invoke the TokenEx.Iframe() method to generate a new iframe object.
                  This method accepts two parameters: the ID of the container in which you want to
                  render the iframe and the iFrame Configuration Object you created.
                </p>
                <CopyBlock
                  text={`var iframe = new TokenEx.Iframe("tokenExIframeDiv", iframeConfig); 
// Add event listeners here 
iframe.load();`}
                  theme={dracula}
                  showLineNumbers={false}
                  language="JavaScript"
                  customStyle={{ overflowY: 'auto' }}
                />
                <h2 className="mt-5 font-normal text-black">
                  Submitting The Data for Tokenization
                </h2>
                <CopyBlock
                  text={`document.getElementById('tokenExIframeDiv').addEventListener('submit', (event) => {
  event.preventDefault();     // stop normal form submit
  iframe.tokenize();          // trigger TokenEx tokenization
});`}
                  theme={dracula}
                  showLineNumbers={false}
                  language="JavaScript"
                  customStyle={{ overflowY: 'auto' }}
                />
              </TabPanel>
              <TabPanel value={value} index={1}>
                <h2 className="text-black large">Using Custom Data Types:</h2>
                <p className="text-black light">
                  Using the custom data type feature enables you to extend/alter the default
                  validation logic when in PCI mode based on the card types you accept, including
                  private-label and country-specific card brands.
                </p>
                <p className="text-black light">
                  You can supply an array of custom types in your configuration object's
                  customDataTypes property.
                </p>
                <CustomDataTypes />
                <CopyBlock
                  text={`var iframeConfig = { 
    origin: "https://mysite.com", 
    timestamp: "20180109161437", 
    tokenExID: "REMOVED", 
    authenticationKey: "REMOVED", 
    pci: true, 
    cvv: true, 
    customDataTypes: [
        { 
        type: "privateCard", 
        validRegex: "^(5019)\\d{12}$", 
        possibleRegEx: "^(5019)\\d+$", 
        maxLength: 16, 
        cvvValidRegex: "^[0-9]{2}$", 
        cvvMaxLength: 2, 
        cvvRequired: false, 
        luhnCheck: true 
        } 
    ], 
    ... 
}`}
                  theme={dracula}
                  showLineNumbers={false}
                  language="JavaScript"
                  customStyle={{ overflowY: 'auto' }}
                />
              </TabPanel>
              <TabPanel value={value} index={2}>
                <h2 className="text-black large">Styling the iFrame:</h2>
                <p className="text-black light">
                  The iFrame is styled by passing the CSS in the configuration object used to
                  generate the iFrame. The styles object consists of three elements: base, focus and
                  error.
                </p>
                <StylesParams />
                <CopyBlock
                  text={`var styles = { 
    base: "font-family: Arial, sans-serif;padding: 0 8px;border: 1px solid rgba(0, 0, 0, 0.2);margin: 0;width: 100%;font-size: 13px;line-height: 30px;height: 32px;box-sizing: border-box;-moz-box-sizing: border-box;", 
    focus: "box-shadow: 0 0 6px 0 rgba(0, 132, 255, 0.5);border: 1px solid rgba(0, 132, 255, 0.5);outline: 0;", 
    error: "box-shadow: 0 0 6px 0 rgba(224, 57, 57, 0.5);border: 1px solid rgba(224, 57, 57, 0.5);" 
}`}
                  theme={dracula}
                  showLineNumbers={false}
                  language="JavaScript"
                  customStyle={{ overflow: 'auto' }}
                />
                <p className="text-black light">
                  In general, you should provide a class for the input, as well as an error class,
                  which the iFrame will automatically append if a validation failure occurs.
                </p>
                <p className="text-black light">
                  Using the configuration depicted in the previous code sample would result in the
                  following CSS applied within the iFrame:
                </p>
                <CopyBlock
                  text={`input { 
    font-family: Arial, sans-serif; 
    padding: 0 8px; 
    border: 1px solid rgba(0, 0, 0, 0.2); 
    margin: 0; width: 100%; 
    font-size: 13px; 
    line-height: 30px; 
    height: 32px; 
    box-sizing: border-box;
    -moz-box-sizing: border-box; 
} 

input.focus { 
    box-shadow: 0 0 6px 0 rgba(0, 132, 255, 0.5); 
    border: 1px solid rgba(0, 132, 255, 0.5); 
    outline: 0; 
}
 
input.error { 
    box-shadow: 0 0 6px 0 rgba(224, 57, 57, 0.5); 
    border: 1px solid rgba(224, 57, 57, 0.5); 
}`}
                  theme={dracula}
                  language="css"
                  showLineNumbers={true}
                  customStyle={{ overflow: 'auto' }}
                />
              </TabPanel>
            </Box>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default DeveloperTokenizer;
